import React from 'react';
import PropTypes from 'prop-types';
import lodashCompact from 'lodash/fp/compact';
import lodashFindIndex from 'lodash/findIndex';

import { getSecurityDepositSettings, getMarketSecurityDepositLabel } from 'common/listingHelpers';

import {
  LISTING_AGREEMENT_MANUAL,
  LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_MANUAL,
} from 'common/listingConstants';

import { isApplicationAccepted } from 'common/householdApplication/applicationHelpers';

import SrMultiStepForm, {
  SR_MULTI_STEP_FORM_THEME_PARTNER_PAGE,
} from 'client/components/SrMultiStepForm';
import SrPartnerApprovalSubmit from './SrPartnerApprovalSubmit';
import SrPartnerApprovalContext from './SrPartnerApprovalContext';
import SrPartnerApprovalControl from './SrPartnerApprovalControl';
import SrPartnerApprovalFormReview from './SrPartnerApprovalFormReview';
import SrPartnerApprovalFormStepSimple from './SrPartnerApprovalFormStepSimple';
import SrPartnerApprovalFormStepAddenda from './SrPartnerApprovalFormStepAddenda';
import SrPartnerApprovalSecurityDepositIntoText from './SrPartnerApprovalSecurityDepositIntoText';

import makePartnerApprovalFormFieldsGetterFunc from './makePartnerApprovalFormFieldsGetterFunc';

const makeFormStepsGetterFunc = ({ listing, market, partner }) => {
  const {
    firstMonthRentEnabled,
    firstMonthRentCollectionTimeEnabled,
    leasingInformationEnabled,
    listingAgreementEnabled,
    whoCollectsRentPaymentEnabled,
    rentPaymentDescriptionEnabled,
    additionalAddendaEnabled,
    partnerLeaseNotesEnabled,
    collectRbpFeeEnabled,
    collectLastMonthRentEnabled,
    collectAdminFeeEnabled,
  } = partner.experienceConfiguration;

  const securityDepositLabel = getMarketSecurityDepositLabel(market);

  const { whoCollectsSecurityDepositPetFees } = getSecurityDepositSettings({
    listing,
    partner,
  });

  const shouldShowCollectFirstMonthQuestion =
    firstMonthRentEnabled &&
    whoCollectsSecurityDepositPetFees !== LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_MANUAL;

  const shouldShowCollectRbpFeeQuestion =
    collectRbpFeeEnabled &&
    whoCollectsSecurityDepositPetFees !== LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_MANUAL;

  const shouldShowCollectLastMonthRentQuestion =
    collectLastMonthRentEnabled &&
    whoCollectsSecurityDepositPetFees !== LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_MANUAL;

  const shouldShowCollectAdminFeeQuestion =
    collectAdminFeeEnabled &&
    whoCollectsSecurityDepositPetFees !== LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_MANUAL;

  return ({ values }) => {
    const propertyOwnerEnabled =
      (partner.experienceConfiguration.propertyOwnerNameEnabled ||
        partner.experienceConfiguration.propertyOwnerEmailEnabled ||
        partner.experienceConfiguration.propertyOwnerPhoneEnabled ||
        partner.experienceConfiguration.managementEnabled ||
        partner.experienceConfiguration.managementNameEnabled ||
        partner.experienceConfiguration.managementEmailEnabled ||
        partner.experienceConfiguration.managementPhoneEnabled ||
        partner.experienceConfiguration.repairContactEnabled ||
        partner.experienceConfiguration.repairContactNameEnabled ||
        partner.experienceConfiguration.repairContactEmailEnabled ||
        partner.experienceConfiguration.repairContactPhoneEnabled) &&
      (values.listingAgreementType === LISTING_AGREEMENT_MANUAL || !listingAgreementEnabled);

    const rentPaymentEnabled = whoCollectsRentPaymentEnabled || rentPaymentDescriptionEnabled;

    const steps = {
      'security-deposit': {
        ui: {
          pageHeader: `Need to increase the ${securityDepositLabel}?`,
          pageIntroText: (
            <SrPartnerApprovalSecurityDepositIntoText securityDepositLabel={securityDepositLabel} />
          ),
        },
        fieldIds: ['securityDepositIncreaseMultiplier', 'securityDepositIncreaseReason'],
      },
      'collect-rent': {
        ui: {
          pageHeader: 'Rent Price',
          pageIntroText: !shouldShowCollectFirstMonthQuestion
            ? ''
            : 'Sunroom will only assist in collecting the move-in costs. These will be collected ' +
              `and transferred to your connected account with the ${securityDepositLabel} payment. ` +
              'You will receive an email confirming the renter’s payment and a breakdown of ' +
              'the total payment.',
        },
        fieldIds: lodashCompact([
          shouldShowCollectFirstMonthQuestion && 'collectFirstMonth',
          'rentPrice',
          shouldShowCollectFirstMonthQuestion && firstMonthRentCollectionTimeEnabled
            ? 'collectFirstMonthTime'
            : null,
          shouldShowCollectRbpFeeQuestion && 'collectRbpFee',
          shouldShowCollectAdminFeeQuestion && 'collectAdminFee',
          shouldShowCollectLastMonthRentQuestion && 'collectLastMonthRent',
        ]),
      },
      'listing-agreement': {
        ui: {
          pageHeader: 'Lease Drafting',
          pageIntroText:
            'In order to draft the lease for this listing we need a bit more information.',
        },
        fieldIds: ['listingAgreementType', 'listingAgreementUrl'],
      },
      'property-owner': {
        ui: {
          pageHeader: 'Lease Drafting',
          pageIntroText:
            'Upon approval our team will start drafting the lease for this listing. ' +
            ' This info will be shared with the renter on the lease.',
        },
        fieldIds: [
          'propertyOwnerType',
          'propertyOwnerName',
          'propertyOwnerPhone',
          'propertyOwnerEmail',
          'isManagementSameAsPropertyOwner',
          'managementName',
          'managementEmail',
          'managementPhone',
          'isRepairSameAsManagement',
          'repairContactName',
          'repairContactEmail',
          'repairContactPhone',
        ],
      },
      'rent-payments': {
        ui: {
          pageHeader: 'Lease Drafting',
          pageIntroText:
            'Sunroom DOES NOT collect ongoing rent payments while the renter is living there.' +
            ' We are gathering this info for drafting the lease.',
        },
        fieldIds: ['whoCollectsRentPayment', 'rentPaymentDescription'],
      },
      'extra-notes': {
        ui: {
          pageHeader: 'Lease Drafting',
        },
        fieldIds: ['partnerLeaseNotes'],
      },
      addenda: {
        component: SrPartnerApprovalFormStepAddenda,
        ui: {
          pageHeader: 'Addenda to Include',
        },
        fieldIds: ['additionalAddenda'],
      },
      summary: {
        component: SrPartnerApprovalFormReview,
        ui: {
          pageHeader: 'Review & Approve',
          pageIntroText: `Sunroom will notify the renter to pay the ${securityDepositLabel} & other fees listed below.`,
          controlComponent: SrPartnerApprovalSubmit,
        },
        fieldIds: [],
      },
    };

    const stepIds = lodashCompact([
      'security-deposit',
      'collect-rent',
      ...(leasingInformationEnabled
        ? lodashCompact([
            listingAgreementEnabled && 'listing-agreement',
            propertyOwnerEnabled && 'property-owner',
            rentPaymentEnabled && 'rent-payments',
            additionalAddendaEnabled && 'addenda',
            partnerLeaseNotesEnabled && 'extra-notes',
          ])
        : []),
      'summary',
    ]);

    return stepIds
      .map((stepId) => {
        return {
          id: stepId,
          ...steps[stepId],
        };
      })
      .map((step) => ({
        component: SrPartnerApprovalFormStepSimple,
        ...step,
        ui: {
          navbarTitle: 'Approve Application',
          controlComponent: SrPartnerApprovalControl,
          theme: SR_MULTI_STEP_FORM_THEME_PARTNER_PAGE,
          ...step.ui,
        },
      }));
  };
};

const SrPartnerApprovalForm = ({
  partner,
  listing,
  market,
  currentUser,
  householdListing,
  householdApplications,
  householdGuarantors,
  landlord,
  concession,
  listingStripeAccount,
  partnerStripeAccount,
  history,
  location,
  isBusy,
  dispatch,
  onBack,
  onSubmit,
  onStepChange,
  openShowContactSupportModal,
}) => {
  const defaultValues = {
    securityDepositIncreaseMultiplier: householdListing.securityDepositIncreaseMultiplier || 1,
    securityDepositIncreaseReason: householdListing.securityDepositIncreaseReason,
    rentPrice: householdListing.rentPrice ?? listing.price,
    collectFirstMonthTime:
      listing.collectFirstMonthTime ?? partner.experienceConfiguration.collectFirstMonthTime, // eslint-disable-line max-len
    collectFirstMonth:
      typeof listing.collectFirstMonth === 'boolean' ? listing.collectFirstMonth : null,
    collectLastMonthRent:
      typeof listing.collectLastMonthRent === 'boolean' ? listing.collectLastMonthRent : null, // eslint-disable-line max-len
    collectRbpFee: typeof listing.collectRbpFee === 'boolean' ? listing.collectRbpFee : null,
    collectAdminFee: typeof listing.collectAdminFee === 'boolean' ? listing.collectAdminFee : null,
    listingAgreementType: listing.listingAgreementType,
    listingAgreementUrl: listing.listingAgreementUrl,
    propertyOwnerType: listing.propertyOwnerType,
    propertyOwnerName: listing.propertyOwnerName ?? landlord?.propertyOwnerName,
    propertyOwnerPhone: listing.propertyOwnerPhone ?? landlord?.propertyOwnerPhone,
    propertyOwnerEmail: listing.propertyOwnerEmail ?? landlord?.propertyOwnerEmail,
    isManagementSameAsPropertyOwner: listing.isManagementSameAsPropertyOwner,
    managementName: listing.managementName,
    managementPhone: listing.managementPhone,
    managementEmail: listing.managementEmail,
    isRepairSameAsManagement: listing.isRepairSameAsManagement,
    repairContactName: listing.repairContactName,
    repairContactPhone: listing.repairContactPhone,
    repairContactEmail: listing.repairContactEmail,
    whoCollectsRentPayment: listing.whoCollectsRentPayment,
    rentPaymentDescription: listing.rentPaymentDescription,
    additionalAddenda: householdListing.additionalAddenda,
    partnerLeaseNotes: householdListing.partnerLeaseNotes,
  };

  const contextValue = {
    history,
    partner,
    listing,
    concession,
    market,
    currentUser,
    householdListing,
    householdApplications,
    householdGuarantors,
    listingStripeAccount,
    partnerStripeAccount,
    openShowContactSupportModal,
  };
  const getFormSteps = makeFormStepsGetterFunc({
    listing,
    market,
    partner,
  });
  const getFormFields = makePartnerApprovalFormFieldsGetterFunc({
    partner,
    listing,
    securityDepositLabel: getMarketSecurityDepositLabel(market),
    householdListing,
    householdApplications,
    householdGuarantors,
  });

  const householdIsApproved = householdApplications.every(isApplicationAccepted);
  const stepId = householdIsApproved ? 'summary' : location.pathname.split('/').pop();
  const formSteps = getFormSteps({ values: defaultValues });
  const defaultStep = Math.max(
    lodashFindIndex(formSteps, (step) => step.id === stepId),
    0,
  );

  return (
    <>
      <SrPartnerApprovalContext.Provider value={contextValue}>
        <SrMultiStepForm
          submitOnNext
          validateOnClickNext
          canGoBackFromInitialStep={!householdIsApproved}
          validateOnChange={false}
          confirmLeave={false}
          dispatch={dispatch}
          defaultStep={defaultStep}
          defaultValues={defaultValues}
          getFormSteps={getFormSteps}
          getFormFields={getFormFields}
          isBusy={isBusy}
          onBack={onBack}
          onSubmit={onSubmit}
          onStepChange={onStepChange}
        />
      </SrPartnerApprovalContext.Provider>
    </>
  );
};

SrPartnerApprovalForm.propTypes = {
  partner: PropTypes.object.isRequired,
  listing: PropTypes.object.isRequired,
  concession: PropTypes.object,
  currentUser: PropTypes.object.isRequired,
  landlord: PropTypes.object,
  householdListing: PropTypes.object.isRequired,
  householdApplications: PropTypes.array.isRequired,
  householdGuarantors: PropTypes.array.isRequired,
  listingStripeAccount: PropTypes.object,
  partnerStripeAccount: PropTypes.object,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  isBusy: PropTypes.bool,
  dispatch: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onStepChange: PropTypes.func.isRequired,
  openShowContactSupportModal: PropTypes.func.isRequired,
};

SrPartnerApprovalForm.displayName = 'SrPartnerApprovalForm';

export default SrPartnerApprovalForm;
